import React, { useState, useEffect, useRef } from 'react';
import './masteraccount.css';
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import MasterLayout from './components/MasterLayout';
import MusicUploader from './musicuploader';
import MusicHistory from './musichistory';
import Channels from './channels';
import TalentRequest from './talentrequest';
import InProduction from './inproduction';
import MessagingSchedule from './messagingschedule';
import AccountPage from './accountpage';
import Locations from './locations';
import Billing from './billing';
import FAQ from './faq';
import { CLIENT_SUPPORT_HUB_PATH } from './supportHubPaths';
import UserManagementPage from './pages/UserManagementPage';
import LegacyClientGlobalRedirect from './components/LegacyClientGlobalRedirect';

const MasterAccount = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '' });
  const [roles, setRoles] = useState([
    {
      name: 'Administrator',
      description: 'Full system access',
      permissions: 'All',
      users: '3'
    }
  ]);
  const [activeRoleMenu, setActiveRoleMenu] = useState(null);
  const actionsRef = useRef(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const assignModalRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: '',
    auth: 'Two-step',  // Default value
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [activeUserMenu, setActiveUserMenu] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setActiveRoleMenu(null);
      }
      if (assignModalRef.current && !assignModalRef.current.contains(event.target)) {
        setShowAssignModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Do not invent demo roster identities — users list stays empty until API-backed.

  const handleCreateRoleClick = () => {
    setShowNewRoleModal(true);
  };

  const handleCloseModal = () => {
    setShowNewRoleModal(false);
    setNewRole({ name: '', description: '' });
  };

  const handleCreateRole = () => {
    setRoles([...roles, {
      name: newRole.name,
      description: newRole.description,
      permissions: 'None',
      users: '0'
    }]);
    handleCloseModal();
  };

  const handleRoleMenuClick = (index) => {
    setActiveRoleMenu(activeRoleMenu === index ? null : index);
  };

  const handleDeleteRole = (index) => {
    const updatedRoles = roles.filter((_, i) => i !== index);
    setRoles(updatedRoles);
    setActiveRoleMenu(null);
  };

  const handleAssignToUsers = (role, index) => {
    setSelectedRole(role);
    setShowAssignModal(true);
    setActiveRoleMenu(null);
  };

  const handleDropdownArrowClick = (e) => {
    e.stopPropagation();
    if (searchTerm) {  // Only show dropdown if there's text
      setShowUserDropdown(true);
    }
  };

  const handleUserSearch = (e) => {
    setSearchTerm(e.target.value);
    setShowUserDropdown(true);
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddUserClick = () => {
    setShowNewUserModal(true);
  };

  const handleCreateUser = () => {
    setUsers([...users, {
      name: newUser.name,
      email: newUser.email,
      role: newUser.role,
      auth: newUser.auth,
      lastLogin: '-',
      avatar: null
    }]);
    setShowNewUserModal(false);
    setNewUser({ name: '', email: '', role: '', auth: 'Two-step' });
  };

  const handleAssignRole = () => {
    if (selectedUser && selectedRole) {
      setUsers(users.map(user => {
        if (user.name === selectedUser) {
          return { ...user, role: selectedRole.name };
        }
        return user;
      }));
      setShowAssignModal(false);
      setSearchTerm('');
      setSelectedUser(null);
    }
  };

  const handleEditRole = (role, index) => {
    setEditingRole({ ...role, index });
    setShowEditRoleModal(true);
    setActiveRoleMenu(null);
  };

  const handleSaveEdit = () => {
    const updatedRoles = roles.map((role, index) => {
      if (index === editingRole.index) {
        return {
          ...role,
          name: editingRole.name,
          description: editingRole.description,
          permissions: editingRole.permissions
        };
      }
      return role;
    });
    setRoles(updatedRoles);
    setShowEditRoleModal(false);
    setEditingRole(null);
  };

  const handleUserMenuClick = (index, event) => {
    event.stopPropagation();
    setActiveUserMenu(activeUserMenu === index ? null : index);
  };

  const handleDeleteUser = (index) => {
    const updatedUsers = users.filter((_, i) => i !== index);
    setUsers(updatedUsers);
    setActiveUserMenu(null);
  };

  const handleEditUser = (user, index) => {
    setEditingUser({ ...user, index });
    setShowEditUserModal(true);
    setActiveUserMenu(null);
  };

  const handleSaveUserEdit = () => {
    const updatedUsers = users.map((user, index) => {
      if (index === editingUser.index) {
        return {
          ...user,
          name: editingUser.name,
          email: editingUser.email,
          role: editingUser.role,
          auth: editingUser.auth
        };
      }
      return user;
    });
    setUsers(updatedUsers);
    setShowEditUserModal(false);
    setEditingUser(null);
  };

  return (
    <MasterLayout>
      <Routes>
        <Route path="/" element={<UserManagementPage />} />
        <Route path="/musicuploader" element={<MusicUploader />} />
        <Route path="/musichistory" element={<MusicHistory />} />
        <Route path="/channels" element={<Channels />} />
        <Route path="/talent-request" element={<TalentRequest />} />
        <Route path="/in-production" element={<InProduction />} />
        <Route path="/messaging-schedule" element={<MessagingSchedule />} />
        <Route
          path="/account/*"
          element={
            <LegacyClientGlobalRedirect>
              <AccountPage />
            </LegacyClientGlobalRedirect>
          }
        />
        <Route path="/locations" element={<Locations />} />
        <Route path="/billing" element={<Billing />} />
        <Route
          path="/support"
          element={<Navigate to={CLIENT_SUPPORT_HUB_PATH} replace />}
        />
        <Route path="/faq" element={<FAQ />} />
        {/* Brand-scoped User Management (same page component as BM global UM) */}
        <Route path="/user-management" element={<UserManagementPage />} />
        <Route
          path="/user-management/users"
          element={<UserManagementPage activeTab="users" />}
        />
        <Route
          path="/user-management/roles"
          element={<UserManagementPage activeTab="roles" />}
        />
      </Routes>

      {showNewRoleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>New Role</h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                  placeholder="Enter role name"
                />
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <input
                  type="text"
                  value={newRole.description}
                  onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                  placeholder="Enter role description"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={handleCloseModal}>Cancel</button>
              <button className="create-btn" onClick={handleCreateRole}>Create</button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content" ref={assignModalRef}>
            <div className="modal-header">
              <h3>Assign {selectedRole?.name} role to users</h3>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p>Select users you want to assign this role to.</p>
              <div className="form-group">
                <label>Select Users</label>
                <div className="select-input">
                  <input
                    type="text"
                    placeholder="Begin typing to select users..."
                    className="user-select"
                    value={searchTerm}
                    onChange={handleUserSearch}
                    onClick={() => {
                      if (searchTerm) {  // Only show dropdown if there's text
                        setShowUserDropdown(true);
                      }
                    }}
                  />
                  <span 
                    className="dropdown-arrow" 
                    onClick={handleDropdownArrowClick}
                    style={{ cursor: 'pointer' }}
                  >
                    ▼
                  </span>
                  {showUserDropdown && searchTerm && (  // Only show dropdown if there's text
                    <div className="user-search-dropdown">
                      {filteredUsers.map((user, index) => (
                        <div 
                          key={index} 
                          className="user-search-item"
                          onClick={() => {
                            setSearchTerm(user.name);
                            setSelectedUser(user.name);
                            setShowUserDropdown(false);
                          }}
                        >
                          <div className="user-avatar">👤</div>
                          <div className="user-info">
                            <div className="user-name">{user.name}</div>
                            <div className="user-email">{user.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button 
                className="assign-btn" 
                onClick={handleAssignRole}
                disabled={!selectedUser}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewUserModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add User</h3>
              <button className="close-btn" onClick={() => setShowNewUserModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="Enter user name"
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="Enter user email"
                />
              </div>
              
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="role-select"
                >
                  <option value="">Select a role</option>
                  {roles.map((role, index) => (
                    <option key={index} value={role.name}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Authentication</label>
                <select
                  value={newUser.auth}
                  onChange={(e) => setNewUser({...newUser, auth: e.target.value})}
                  className="auth-select"
                >
                  <option value="Two-step">Two-step</option>
                  <option value="Email">Email</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowNewUserModal(false)}>Cancel</button>
              <button className="create-btn" onClick={handleCreateUser}>Create</button>
            </div>
          </div>
        </div>
      )}

      {showEditRoleModal && editingRole && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Role</h3>
              <button className="close-btn" onClick={() => setShowEditRoleModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={editingRole.name}
                  onChange={(e) => setEditingRole({...editingRole, name: e.target.value})}
                  placeholder="Enter role name"
                />
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <input
                  type="text"
                  value={editingRole.description}
                  onChange={(e) => setEditingRole({...editingRole, description: e.target.value})}
                  placeholder="Enter role description"
                />
              </div>

              <div className="form-group">
                <label>Permissions *</label>
                <select
                  value={editingRole.permissions}
                  onChange={(e) => setEditingRole({...editingRole, permissions: e.target.value})}
                  className="permissions-select"
                >
                  <option value="None">None</option>
                  <option value="Limited">Limited</option>
                  <option value="All">All</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowEditRoleModal(false)}>Cancel</button>
              <button className="create-btn" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showEditUserModal && editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="close-btn" onClick={() => setShowEditUserModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  placeholder="Enter user name"
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  placeholder="Enter user email"
                />
              </div>
              
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                  className="role-select"
                >
                  <option value="">Select a role</option>
                  {roles.map((role, index) => (
                    <option key={index} value={role.name}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Authentication</label>
                <select
                  value={editingUser.auth}
                  onChange={(e) => setEditingUser({...editingUser, auth: e.target.value})}
                  className="auth-select"
                >
                  <option value="Two-step">Two-step</option>
                  <option value="Email">Email</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowEditUserModal(false)}>Cancel</button>
              <button className="create-btn" onClick={handleSaveUserEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default MasterAccount; 